/**
 * Marcas FIXAS por deploy.
 *
 * Diferença para `presets.ts`: aqueles são opções que o visitante escolhe no
 * sandbox de white-label, e a escolha vive no localStorage DELE. Isso serve
 * para demonstrar o produto, mas não para entregar uma instância de cliente —
 * numa turma de 30 celulares, cada aparelho abriria com "Minha Escola IA" até
 * alguém configurar um por um.
 *
 * Aqui a marca é propriedade do BUILD (`VITE_BRAND=globall`), não do aparelho:
 * o mesmo código roda em dois deploys com identidades diferentes, apontando
 * para o mesmo Supabase (decisão D2 da spec).
 *
 * `VITE_BRAND` ausente = comportamento atual, sem nenhuma mudança.
 *
 * Spec: .planning/MODO-CLASSE-SPEC.md §2 (D2)
 */
import type { WhiteLabelConfig } from './types';

const terminologiaPtBr = {
  learnerSingular: 'aluno',
  learnerPlural: 'alunos',
  lessonSingular: 'aula',
  lessonPlural: 'aulas',
  moduleSingular: 'módulo',
  modulePlural: 'módulos',
  missionSingular: 'missão',
  missionPlural: 'missões',
};

/**
 * Paleta da arte oficial (brand/brand-notes.md do projeto globall):
 * azul-marinho profundo + azul elétrico/roxo + vermelho + branco.
 *
 * O teal #2ee6a8 das composições NÃO entra aqui de propósito: ele é a cor do
 * bloco "T" do framework ACT dentro das aulas, não uma cor da marca.
 */
const globallPalette: [string, string, string, string] = [
  '#4cc9f0', // azul elétrico
  '#a78bfa', // roxo
  '#e63946', // vermelho (o "ll" final do logo)
  '#050b1e', // azul-marinho profundo
];

/**
 * Textos vindos da arte oficial recebida em 2026-07-13 — nada inventado aqui.
 * Programa: "Globall NEXTGEN: AI & Vibe Coding — A Próxima Geração de
 * Desenvolvimento", com os 4 pilares como mapa do curso.
 */
export const GLOBALL_BRAND: WhiteLabelConfig = {
  presetId: 'custom',
  brandName: 'Globall NEXTGEN',
  legalName: 'Globall',
  logoEmoji: '🌐', // o logo traz um globo no primeiro "o" de Globall
  tagline: 'AI & Vibe Coding — A Próxima Geração de Desenvolvimento',
  /* Assinatura da marca, trocada pelo José em 12/08. O texto anterior descrevia
     a EMENTA do curso ("Fundamentos da IA Generativa e Engenharia de Prompt…")
     em três linhas na primeira tela — é grade curricular, não promessa, e quem
     abre o login já sabe em que curso se matriculou.

     Fica em inglês de propósito: é slogan de marca, não terminologia de
     interface. A armadilha nº13 do CLAUDE.md ("modo classe é PT-BR fixo") é
     sobre `labels.*` virarem "comece a lesson" — não sobre a assinatura. */
  valueProposition: 'Learn Today. Lead Tomorrow.',
  subjectArea: 'IA generativa e vibe coding',
  aiTutorName: 'Tutor',
  aiTutorRoleDescription: 'tutor do Globall NEXTGEN',
  certificateProgramFallback: 'Globall NEXTGEN: AI & Vibe Coding',
  certificateIssuer: 'Globall',
  accentPalette: globallPalette,
  terminology: terminologiaPtBr,
};

export const FIXED_BRANDS: Record<string, WhiteLabelConfig> = {
  globall: GLOBALL_BRAND,
};

/** Marca fixada no build, se houver. */
export function resolveFixedBrand(raw: string | undefined): WhiteLabelConfig | null {
  if (!raw) return null;
  return FIXED_BRANDS[raw.toLowerCase().trim()] ?? null;
}
